import React, { useRef, useState, useCallback } from 'react'
import { describeMood, getQuadrantColor } from './moodCompassInterpretation.js'

function QuadrantLabel({ text, top, left }) {
  return (
    <div
      style={{
        position: 'absolute',
        top,
        left,
        transform: 'translate(-50%, -50%)',
        fontSize: 8,
        fontWeight: 500,
        color: 'var(--color-text-tertiary)',
        letterSpacing: '0.02em',
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      {text}
    </div>
  )
}

function Dot({ x, y, disabled }) {
  const borderColor = getQuadrantColor(x, y)

  return (
    <div
      data-testid="mood-dot"
      style={{
        position: 'absolute',
        top: `${y * 100}%`,
        left: `${x * 100}%`,
        transform: 'translate(-50%, -50%)',
        width: 14,
        height: 14,
        borderRadius: '50%',
        background: 'white',
        border: `2.5px solid ${borderColor}`,
        pointerEvents: 'none',
        opacity: disabled ? 0.4 : 1,
      }}
    />
  )
}

// Stored format is integer percent (0-100) so DynamoDB (which rejects
// Python floats via the boto3 resource API) can serialize the auto-saved
// canvas. Internal rendering math still uses floats in [0, 1].
function toStored(coords) {
  return {
    x: Math.round(coords.x * 100),
    y: Math.round(coords.y * 100),
  }
}

function GridCanvas({ value, onChange, disabled }) {
  const gridRef = useRef(null)
  const [isFocused, setIsFocused] = useState(false)

  const computeCoords = useCallback((clientX, clientY) => {
    const rect = gridRef.current.getBoundingClientRect()
    const x = (clientX - rect.left) / rect.width
    const y = (clientY - rect.top) / rect.height
    return {
      x: Math.max(0, Math.min(1, x)),
      y: Math.max(0, Math.min(1, y)),
    }
  }, [])

  const handleClick = useCallback((event) => {
    if (disabled) return
    onChange(toStored(computeCoords(event.clientX, event.clientY)))
  }, [disabled, onChange, computeCoords])

  const handleTouchStart = useCallback((event) => {
    if (disabled) return
    event.preventDefault()
    const touch = event.touches[0]
    onChange(toStored(computeCoords(touch.clientX, touch.clientY)))
  }, [disabled, onChange, computeCoords])

  const handleKeyDown = useCallback((event) => {
    if (disabled) return
    const step = 0.05
    const current = value || { x: 0.5, y: 0.5 }
    let nextX = current.x
    let nextY = current.y
    let handled = true

    switch (event.key) {
      case 'ArrowLeft':  nextX = Math.max(0, current.x - step); break
      case 'ArrowRight': nextX = Math.min(1, current.x + step); break
      case 'ArrowUp':    nextY = Math.max(0, current.y - step); break
      case 'ArrowDown':  nextY = Math.min(1, current.y + step); break
      case 'Home':       nextX = 0.5; nextY = 0.5; break
      case 'Enter':
      case ' ':
        if (!value) { nextX = 0.5; nextY = 0.5 }
        else { handled = false }
        break
      default:
        handled = false
    }

    if (handled) {
      event.preventDefault()
      onChange(toStored({ x: nextX, y: nextY }))
    }
  }, [disabled, value, onChange])

  const ariaLabel = value
    ? `Mood grid. Current position: ${describeMood(value.x, value.y)}. Tap or use arrow keys to change.`
    : 'Mood grid. Tap anywhere to place your mood, or use arrow keys.'

  return (
    <div
      ref={gridRef}
      data-testid="mood-grid-canvas"
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onKeyDown={handleKeyDown}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      role="application"
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      style={{
        flex: 1,
        position: 'relative',
        aspectRatio: '2 / 1',
        background: 'white',
        border: '0.5px solid #c8ccd4',
        borderRadius: 6,
        cursor: disabled ? 'default' : 'pointer',
        minWidth: 0,
        opacity: disabled ? 0.4 : 1,
        outline: isFocused ? '2px solid var(--color-text-info)' : 'none',
        outlineOffset: 2,
      }}
    >
      {/* Horizontal crosshair */}
      <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 0, borderTop: '0.5px solid rgba(68,68,65,0.12)', pointerEvents: 'none' }} />

      {/* Vertical crosshair */}
      <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 0, borderLeft: '0.5px solid rgba(68,68,65,0.12)', pointerEvents: 'none' }} />


      {/* Quadrant labels */}
      <QuadrantLabel text="Excited" top="17.5%" left="17.5%" />
      <QuadrantLabel text="Stressed" top="17.5%" left="82.5%" />
      <QuadrantLabel text="Calm" top="82.5%" left="17.5%" />
      <QuadrantLabel text="Drained" top="82.5%" left="82.5%" />

      {/* The dot */}
      {value && <Dot x={value.x} y={value.y} disabled={disabled} />}
    </div>
  )
}

export function MoodCompassGrid({ value, onChange, disabled }) {
  return (
    <div style={styles.container}>
      {/* Top energy label */}
      <div style={styles.energyLabel}>HIGH ENERGY</div>

      {/* Middle row: left label + grid + right label */}
      <div style={styles.middleRow}>
        {/* Left axis label (vertical, reads bottom-to-top) */}
        <div style={{ ...styles.axisLabelVertical, transform: 'rotate(180deg)' }}>pleasant</div>

        {/* The grid canvas */}
        <GridCanvas value={value} onChange={onChange} disabled={disabled} />

        {/* Right axis label (vertical, reads top-to-bottom) */}
        <div style={styles.axisLabelVertical}>unpleasant</div>
      </div>

      {/* Bottom energy label */}
      <div style={styles.energyLabel}>LOW ENERGY</div>

      {/* Empty-state hint */}
      {!value && !disabled && (
        <div style={styles.emptyHint}>Tap anywhere in the grid</div>
      )}

      {/* Hidden live region for screen readers */}
      <div aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}>
        {value ? describeMood(value.x, value.y) : ''}
      </div>
    </div>
  )
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 14px 12px',
    position: 'relative',
  },
  middleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  axisLabelVertical: {
    writingMode: 'vertical-rl',
    fontSize: 9,
    fontWeight: 500,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--color-text-tertiary)',
    flexShrink: 0,
  },
  energyLabel: {
    textAlign: 'center',
    fontSize: 9,
    fontWeight: 500,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--color-text-tertiary)',
    marginTop: 2,
    marginBottom: 2,
  },
  emptyHint: {
    textAlign: 'center',
    fontSize: 11,
    fontStyle: 'italic',
    color: 'var(--color-text-tertiary)',
    marginTop: 4,
  },
}
